import { useEffect, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { EcardService } from '@klangio/ai2notes-ts-client';
import AccountCircle from '@mui/icons-material/AccountCircle';
import PersonAdd from '@mui/icons-material/PersonAdd';
import {
  Container, Divider, Paper, Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Helmet } from 'react-helmet-async';
import { GradientFab } from '../components/atoms';
import { ComplianzCookieConsent } from '../components/molecules';
import RedeemCodeForm from '../components/molecules/RedeemCodeForm';
import { AdvancedFooter, NavigationHeader } from '../components/organisms';
import { type WithUser, withUserContext } from '../context';
import useLocaleUrl from '../hooks/useLocaleUrl';
import { type ITheme } from '../theme';
import { getParameterByName } from '../utils';

const useStyles = makeStyles()((theme: ITheme) => ({
  app: {
    textAlign: 'center',
    color: '#FFFFFF',
  },
  title: {
    marginTop: 30,
    fontSize: '2.8em',
    lineHeight: '1.15em',
    fontWeight: theme.typography.fontWeightMedium as number,
    marginBottom: 80,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 40,
    },
  },
  section: {
    textAlign: 'center',
    backgroundImage: theme.gradients.primaryGradient,
  },
  content: {
    paddingBottom: 80,
    minHeight: 'calc(100vh - 150px)',
  },
  paper: {
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: 50,
  },
  verticalMargin: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  marginTop: {
    marginTop: theme.spacing(4),
  },
  descriptionWrapper: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: theme.spacing(4),
    alignItems: 'center',
  },
  descriptionIcon: {
    marginRight: theme.spacing(4),
  },
  description: { textAlign: 'left' },
}));

export type RedeemProps = WithUser;

const Redeem: React.FC<RedeemProps> = ({ user }) => {
  const [code, setCode] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { classes } = useStyles();
  const { t } = useTranslation();
  const localeUrl = useLocaleUrl();

  useEffect(() => {
    if (getParameterByName('code')) {
      setCode(getParameterByName('code'));
    }
  }, []);

  const handleSubmit = () => {
    setSubmitting(true);

    EcardService.ecardRedeem(code)
      .then(() => {
        const amount = 'Redeemed'; /// TODO
        window.location.href = localeUrl(`/?msg=tickets_added&count=${amount}`);
      })
      .catch(() => {
        setSubmitting(false);
        setError('Code not valid or already redeemed!');
      });
  };

  const handleOnClickLogin = () => {
    window.location.href = `https://auth.klang.io/login?redirect_url=${encodeURIComponent(
      window.location as unknown as string,
    )}`;
  };

  const handleOnClickRegister = () => {
    window.location.href = `https://auth.klang.io/register?redirect_url=${encodeURIComponent(
      window.location as unknown as string,
    )}`;
  };

  return (
    <>
      <Helmet>
        <title>{t('redeem.page_title')}</title>
      </Helmet>
      <ComplianzCookieConsent />
      <div className={classes.app}>
        <NavigationHeader />
        <section id="section" className={classes.section}>
          <Container maxWidth="sm" className={classes.content}>
            {user ? (
              <>
                <Typography className={classes.title} variant="h1" component="h1">
                  {t('redeem.title_cta')}
                </Typography>
                <RedeemCodeForm
                  code={code}
                  errorText={error}
                  submitted={submitting}
                  onHandleSubmit={handleSubmit}
                  onChangeCode={(e) => setCode(e.target.value)}
                />
              </>
            ) : (
              <>
                <Typography className={classes.title} variant="h1" component="h1">
                  {t('redeem.login_title')}
                </Typography>
                <Paper className={classes.paper} elevation={0}>
                  <Typography>{t('redeem.sign_in_description')}</Typography>
                  <div className={classes.descriptionWrapper}>
                    <AccountCircle
                      className={classes.descriptionIcon}
                      color="primary"
                      fontSize="large"
                    />
                    <Typography variant="subtitle2" className={classes.description}>
                      {t('redeem.login_description')}
                    </Typography>
                  </div>
                  <GradientFab
                    className={classes.marginTop}
                    gradient="primary"
                    onClick={handleOnClickLogin}
                  >
                    {t('redeem.login_btn')}
                  </GradientFab>
                  <Divider className={classes.verticalMargin} />
                  <div className={classes.descriptionWrapper}>
                    <PersonAdd
                      className={classes.descriptionIcon}
                      color="primary"
                      fontSize="large"
                    />
                    <Typography variant="subtitle2" className={classes.description}>
                      {t('redeem.register_description')}
                    </Typography>
                  </div>
                  <GradientFab
                    className={classes.marginTop}
                    gradient="primary"
                    onClick={handleOnClickRegister}
                  >
                    {t('redeem.register_btn')}
                  </GradientFab>
                </Paper>
              </>
            )}
          </Container>
          <AdvancedFooter />
        </section>
      </div>
    </>
  );
};

export default withUserContext(Redeem);
